import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { uploadCompanyLogo } from './postJobApi';
import useAuthStore from '../../stores/authStore';

const COMPANY_SIZES = ['1-10', '11-50', '51-200', '201-500', '500+'];
const API_ORIGIN = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace(/\/api\/?$/, '');

function resolveMediaUrl(url) {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) return url;
  return `${API_ORIGIN}${url.startsWith('/') ? '' : '/'}${url}`;
}

function CompanyInfoStep({ initialData, userEmail, onBack, onNext, saving, token }) {
  const authToken = token || useAuthStore.getState().token;
  const [form, setForm] = useState({
    name: initialData.name || '',
    website: initialData.website || '',
    industry: initialData.industry || '',
    companySize: initialData.companySize || '',
    location: initialData.location || '',
    description: initialData.description || '',
    workEmail: initialData.workEmail || userEmail || '',
    logoUrl: initialData.logoUrl || '',
  });
  const [error, setError] = useState('');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);

  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      name: initialData.name || prev.name,
      website: initialData.website || prev.website,
      industry: initialData.industry || prev.industry,
      companySize: initialData.companySize || prev.companySize,
      location: initialData.location || prev.location,
      description: initialData.description || prev.description,
      workEmail: initialData.workEmail || userEmail || prev.workEmail,
      logoUrl: initialData.logoUrl || prev.logoUrl,
    }));
  }, [initialData, userEmail]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    setError('');
    try {
      const data = await uploadCompanyLogo(authToken, file);
      setForm((prev) => ({ ...prev, logoUrl: data.logoUrl }));
    } catch (err) {
      setError(err.message || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (!form.name.trim()) {
      setError('Company name is required.');
      return;
    }
    if (!form.logoUrl.trim()) {
      setError('Company logo image is required.');
      return;
    }

    onNext(form);
  };

  return (
    <Box className="post-job-card ch-card-enter">
      <Box className="post-job-card-header" sx={{ mb: 1.5 }}>
        <Typography className="post-job-eyebrow" variant="overline" color="primary">
          Step 1 of 3
        </Typography>
        <Typography variant="h5" component="h2" sx={{ mt: 0.25, mb: 0.5 }}>
          Company information
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Tell candidates about your company before you publish the role. A company logo is required.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <Stack spacing={1.25}>
          <Box
            sx={{
              display: 'flex',
              gap: 1.5,
              alignItems: 'center',
              flexWrap: 'wrap',
              p: 1.25,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
            }}
          >
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden',
                bgcolor: '#f8fafc',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {form.logoUrl ? (
                <img
                  src={resolveMediaUrl(form.logoUrl)}
                  alt="Company logo"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <Typography variant="caption" color="text.secondary">
                  Logo
                </Typography>
              )}
            </Box>
            <Box sx={{ flex: 1, minWidth: 180 }}>
              <Typography variant="subtitle2">Company logo *</Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                JPG, PNG, GIF, or WEBP. Max 5MB.
              </Typography>
              <input
                ref={logoInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                hidden
                onChange={handleLogoUpload}
              />
              <Button
                type="button"
                variant="outlined"
                size="small"
                sx={{ mt: 0.75 }}
                disabled={uploadingLogo || saving}
                onClick={() => logoInputRef.current?.click()}
              >
                {uploadingLogo ? 'Uploading...' : form.logoUrl ? 'Change logo' : 'Upload logo'}
              </Button>
            </Box>
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
            <TextField label="Company name" name="name" value={form.name} onChange={handleChange} required />
            <TextField label="Work email" name="workEmail" type="email" value={form.workEmail} onChange={handleChange} required />
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
            <TextField
              label="Company website"
              name="website"
              value={form.website}
              onChange={handleChange}
              placeholder="https://yourcompany.com"
            />
            <TextField
              label="Industry"
              name="industry"
              value={form.industry}
              onChange={handleChange}
              placeholder="SaaS, Fintech, AI..."
            />
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
            <FormControl size="small" fullWidth>
              <InputLabel id="company-size-label">Company size</InputLabel>
              <Select
                labelId="company-size-label"
                id="companySize"
                name="companySize"
                label="Company size"
                value={form.companySize}
                onChange={handleChange}
              >
                <MenuItem value="">
                  <em>Select size</em>
                </MenuItem>
                {COMPANY_SIZES.map((size) => (
                  <MenuItem key={size} value={size}>
                    {size} employees
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Headquarters / location"
              name="location"
              value={form.location}
              onChange={handleChange}
              placeholder="San Francisco, Remote..."
            />
          </Stack>

          <TextField
            label="Company description"
            name="description"
            value={form.description}
            onChange={handleChange}
            multiline
            minRows={4}
            placeholder="What does your company do? Why should talent join?"
          />

          <Box className="post-job-actions" sx={{ mt: 0.5 }}>
            {onBack && (
              <Button type="button" variant="outlined" onClick={onBack}>
                Back
              </Button>
            )}
            <Button type="submit" variant="contained" disabled={saving || uploadingLogo}>
              {saving ? 'Saving...' : 'Continue to job details'}
            </Button>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
}

export default CompanyInfoStep;
